import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { FocusTrap, FocusTrapFactory } from '@angular/cdk/a11y';
import { IconType } from '@ng-icons/core';
import { ResizeService } from 'src/app/@shared/services/resize/resize.service';
import {
  MOT_OVERLAY_PANEL_CLOSED,
  MOT_OVERLAY_PANEL_OPENED,
} from 'src/app/@shared/constants/overlay-panel-a11y.constants';
import { ResizeableComponent } from '../resizeable/resizeable.component';

@Component({
    selector: 'design-slide-up-card',
    templateUrl: './slide-up-card.component.html',
    styleUrls: ['./slide-up-card.component.scss'],
    standalone: false
})
export class SlideUpCardComponent
  extends ResizeableComponent
  implements OnInit, AfterViewInit, OnChanges, OnDestroy
{
  private static _dialogSeq = 0;

  /** Stable id for aria-labelledby → visible header title (when header is rendered). */
  readonly dialogTitleDomId = `rp-dialog-hdg-${SlideUpCardComponent._dialogSeq++}`;

  @ViewChild('cardElement') cardElement: ElementRef;
  @ViewChild('cardElement', { read: ViewContainerRef })
  cardElementViewRef: ViewContainerRef;

  @Output() onHeaderButtonClick: EventEmitter<void> = new EventEmitter<void>();

  @Input() class: string;
  @Input() visible: boolean;
  @Input() showBackgroundImage: boolean;
  @Input() isCardOpen: boolean = true;

  @Input() displayBackground: boolean = false;
  @Input() hideHeaderInDesktop: boolean = false;
  @Input() showLogoInHeader: boolean = false;
  @Input() title: string;
  @Input() icon: IconType;
  /** Pass through to header: first tab stop on back/close icon (overlays like filters). */
  @Input() focusInitialOnHeaderBack: boolean = false;

  /**
   * When true, the card is exposed as an ARIA dialog (modal sheets / overlay panels).
   * Name comes from [title] + header heading, or from [dialogAriaLabel] if there is no header title.
   */
  @Input() roleDialog: boolean = false;

  /**
   * Accessible name when there is no [title] on the planner header (e.g. custom header only).
   * Do not use together with a duplicate visible title.
   */
  @Input() dialogAriaLabel: string | null = null;

  /**
   * a11y: opt-in keyboard focus trap. Not every slide-up-card with
   * roleDialog=true wants to trap focus (e.g. the always-mounted map
   * overlay used as the initial address-picker on mobile, which the user
   * should be able to bypass with Tab to reach the form). Set this to true
   * on the SPECIFIC results dialogs (e.g. search-stations-results-container)
   * that are true overlay modals - then on each false -> true transition
   * of isCardOpen we attach a CDK FocusTrap, pull focus to cdkFocusInitial
   * (when present), and restore focus to the previously-focused element on
   * close. Defaults to false to preserve existing behavior across the
   * dozens of dialogs that already use roleDialog=true.
   */
  @Input() trapFocusOnOpen: boolean = false;

  /**
   * a11y: programmatic CDK FocusTrap. We avoid the cdkTrapFocus directive in
   * the template because slide-up-card instances are frequently kept in the
   * DOM but slid off-screen (isCardOpen=false). The directive would attach a
   * trap regardless of whether the user is intended to interact with that
   * instance right now. With this manual setup we only attach the trap when
   * roleDialog AND isCardOpen are both true, and we explicitly call
   * focusInitialElementWhenReady ONLY on the transition from
   * closed -> open, so always-mounted hidden dialogs (like the
   * address-auto-complete that source-destination-form keeps in the DOM)
   * don't steal focus on page load.
   */
  private focusTrap?: FocusTrap;
  private previouslyFocusedElement: HTMLElement | null = null;
  /** Nested mat-select/autocomplete panels render outside the trap; pause while open. */
  private overlayPanelOpenCount = 0;
  private readonly onOverlayPanelOpened = (): void => this.suspendFocusTrapForOverlay();
  private readonly onOverlayPanelClosed = (): void => this.resumeFocusTrapAfterOverlay();

  constructor(
    protected override readonly resizeService: ResizeService,
    private readonly focusTrapFactory: FocusTrapFactory
  ) {
    super(resizeService);
  }

  override ngOnInit(): void {
    super.ngOnInit();
    if (this.trapFocusOnOpen && typeof document !== 'undefined') {
      document.addEventListener(
        MOT_OVERLAY_PANEL_OPENED,
        this.onOverlayPanelOpened
      );
      document.addEventListener(
        MOT_OVERLAY_PANEL_CLOSED,
        this.onOverlayPanelClosed
      );
    }
  }

  ngAfterViewInit(): void {
    // If the card is born already open AND opted into the focus trap, attach
    // it and pull focus in. This handles overlay-instantiated dialogs (e.g.
    // POI station results) which are created with isCardOpen=true.
    if (this.trapFocusOnOpen && this.roleDialog && this.isCardOpen) {
      this.attachFocusTrap();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.trapFocusOnOpen) {
      return;
    }
    if (!changes['isCardOpen'] && !changes['roleDialog'] && !changes['trapFocusOnOpen']) {
      return;
    }
    if (this.roleDialog && this.isCardOpen) {
      // Only re-attach on a real false -> true transition (or first open).
      const prev = changes['isCardOpen']?.previousValue;
      const becameOpen = prev === false || prev === undefined;
      if (becameOpen) {
        this.attachFocusTrap();
      }
    } else {
      this.detachFocusTrap();
    }
  }

  override ngOnDestroy(): void {
    if (typeof document !== 'undefined') {
      document.removeEventListener(
        MOT_OVERLAY_PANEL_OPENED,
        this.onOverlayPanelOpened
      );
      document.removeEventListener(
        MOT_OVERLAY_PANEL_CLOSED,
        this.onOverlayPanelClosed
      );
    }
    this.detachFocusTrap();
    super.ngOnDestroy();
  }

  private suspendFocusTrapForOverlay(): void {
    if (!this.trapFocusOnOpen || !this.focusTrap) {
      return;
    }
    this.overlayPanelOpenCount++;
    if (this.overlayPanelOpenCount === 1) {
      this.focusTrap.destroy();
      this.focusTrap = undefined;
    }
  }

  private resumeFocusTrapAfterOverlay(): void {
    if (!this.trapFocusOnOpen) {
      return;
    }
    if (this.overlayPanelOpenCount > 0) {
      this.overlayPanelOpenCount--;
    }
    if (
      this.overlayPanelOpenCount === 0 &&
      this.roleDialog &&
      this.isCardOpen &&
      !this.focusTrap
    ) {
      this.attachFocusTrap();
    }
  }

  private attachFocusTrap(): void {
    const el = this.cardElement?.nativeElement as HTMLElement | undefined;
    if (!el) {
      return;
    }
    this.previouslyFocusedElement =
      (document.activeElement as HTMLElement) ?? null;
    this.focusTrap?.destroy();
    this.focusTrap = this.focusTrapFactory.create(el, false);
    void this.focusTrap.focusInitialElementWhenReady().catch(() => {
      /* no focusable element */
    });
  }

  private detachFocusTrap(): void {
    if (!this.focusTrap) {
      return;
    }
    this.focusTrap.destroy();
    this.focusTrap = undefined;
    const toRestore = this.previouslyFocusedElement;
    this.previouslyFocusedElement = null;
    if (toRestore && typeof toRestore.focus === 'function') {
      // Defer to avoid focusing a node that is mid-animation/being removed.
      setTimeout(() => {
        toRestore.focus({ preventScroll: true });
      });
    }
  }

  /** Header row with default title is shown (mobile always when hideHeaderInDesktop). */
  get headerShowsDefaultTitleHeading(): boolean {
    return !this.hideHeaderInDesktop || this.resizeService.isMobile;
  }

  /** Element id for the visible &lt;h2&gt; in app-route-planner-header (for aria-labelledby). */
  get headerDialogTitleElementId(): string | null {
    if (!this.roleDialog || !this.title?.trim()) {
      return null;
    }
    if (!this.headerShowsDefaultTitleHeading) {
      return null;
    }
    return this.dialogTitleDomId;
  }

  get dialogAriaLabelledByAttr(): string | null {
    if (!this.roleDialog) {
      return null;
    }
    return this.headerDialogTitleElementId;
  }

  get dialogAriaLabelAttr(): string | null {
    if (!this.roleDialog) {
      return null;
    }
    if (this.dialogAriaLabelledByAttr) {
      return null;
    }
    const fromInput = this.dialogAriaLabel?.trim();
    if (fromInput) {
      return fromInput;
    }
    const fromTitle = this.title?.trim();
    return fromTitle || null;
  }
}
